/**
 * CORE — steering input quantization (the determinism contract for input).
 *
 * Raw steering arrives as floats in [-1, 1]. Before it can enter a replay it is
 * quantized to a signed-16-bit integer in [-STEER_QUANT, STEER_QUANT], so the
 * recorded timeline is bit-exact across JSON round-trips and platforms. Both the
 * live run (record) and verification (playback) use these exact functions, so
 * the dequantized value the physics sees is identical on client and Worker.
 *
 * Owned by: Core. Internal helper.
 */

import { STEER_QUANT } from '@/types';

/** Clamp raw steer to [-1, 1] then map to integer [-STEER_QUANT, STEER_QUANT]. */
export function quantizeSteer(raw: number): number {
  const clamped = raw < -1 ? -1 : raw > 1 ? 1 : raw;
  // Round half away from zero, deterministically and symmetrically.
  const scaled = clamped * STEER_QUANT;
  const q = scaled < 0 ? -Math.round(-scaled) : Math.round(scaled);
  // Guard against overflow past int16 range.
  return q < -STEER_QUANT ? -STEER_QUANT : q > STEER_QUANT ? STEER_QUANT : q;
}

/** Map a quantized integer steer back to a float in [-1, 1]. */
export function dequantizeSteer(q: number): number {
  return q / STEER_QUANT;
}
